import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useState } from "react";
import {
  foyerApi,
  type VodDownloadPlan,
  type VodTitleRow,
} from "../../http/foyer";
import { tokens } from "../../theme";

/**
 * "Download" on a VOD title: say what will be downloaded, then hand the URL to
 * the browser.
 *
 * The plan is read first because the download itself is a plain navigation
 * into the browser's download manager - the only way a multi-gigabyte film
 * streams to disk instead of into this tab's memory - and a navigation that
 * fails has nowhere to show a reason. So every refusal the server can give
 * (encrypted only, DASH only, CDN refused the manifest, no ffmpeg on the host)
 * is shown HERE, before the operator commits.
 */

type PlanError = {
  code?: string;
  message: string;
  body?: { hint?: string; detail?: string; status?: number };
};

export function planErrorText(err: PlanError): string {
  const body = err.body ?? {};
  switch (err.code) {
    case "download_refused":
      return body.hint ?? "This title cannot be downloaded.";
    case "manifest_refused":
      return `The CDN refused the manifest${body.status ? ` (${body.status})` : ""}, so there is nothing to download from.`;
    case "manifest_unreachable":
      return "The manifest could not be reached from the server.";
    case "manifest_invalid":
      return `The stream URL does not point at a usable HLS playlist${body.detail ? `: ${body.detail}` : "."}`;
    case "manifest_url_invalid":
      return "The title's stream URL is not a valid http(s) address.";
    case "not_found":
      return "This title no longer exists in the catalogue.";
    default:
      return body.hint ?? err.message;
  }
}

function megabits(bps: number | null): string | null {
  if (!bps) return null;
  return `${(bps / 1_000_000).toFixed(bps >= 10_000_000 ? 0 : 1)} Mbps`;
}

function audioText(plan: VodDownloadPlan): string {
  const tracks = plan.audio.map((a) =>
    a.name && a.language
      ? `${a.name} (${a.language})`
      : (a.name ?? a.language ?? "Unnamed track")
  );
  if (plan.muxedAudio) tracks.unshift("carried in the video stream");
  return tracks.join(", ");
}

/** Hand a URL to the browser's download manager. A seam for tests. */
export function startBrowserDownload(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function VodDownloadDialog({
  title,
  onClose,
  download = startBrowserDownload,
}: {
  title: VodTitleRow | null;
  onClose: () => void;
  download?: (url: string) => void;
}) {
  const [plan, setPlan] = useState<VodDownloadPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const assetId = title?.assetId ?? null;

  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setPlan(null);
    setError(null);
    foyerApi
      .vodDownloadPlan(assetId)
      .then((p) => !cancelled && setPlan(p))
      .catch((e: PlanError) => !cancelled && setError(planErrorText(e)));
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  const loading = Boolean(title) && !plan && !error;
  const size =
    plan?.video.width && plan.video.height
      ? `${plan.video.width}×${plan.video.height}`
      : "Single rendition (no ladder)";

  return (
    <Dialog open={title !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Download “{title?.originalTitle || "(no name)"}”
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ py: 2 }}
          >
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 14 }}>
              Reading the manifest for the highest quality…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" data-testid="vod-download-error">
            {error}
          </Alert>
        )}

        {plan && (
          <Stack spacing={1.5} data-testid="vod-download-plan">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: 2,
                rowGap: 0.75,
                fontSize: 14,
              }}
            >
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                Video
              </Typography>
              <Typography
                sx={{ fontSize: 14 }}
                data-testid="vod-download-video"
              >
                {[size, megabits(plan.video.bandwidth)]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                Audio
              </Typography>
              <Typography
                sx={{ fontSize: 14 }}
                data-testid="vod-download-audio"
              >
                {audioText(plan) || "No audio track"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                File
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                }}
              >
                {plan.fileName}
              </Typography>
            </Box>

            {plan.skippedEncrypted && (
              <Alert severity="info" sx={{ fontSize: 13 }}>
                This title also has an encrypted rendition. The file is built
                from the unencrypted copy.
              </Alert>
            )}
            {!plan.available && (
              <Alert severity="warning" sx={{ fontSize: 13 }}>
                This server cannot assemble downloads (ffmpeg is not installed).
              </Alert>
            )}
            {plan.available && plan.busy && (
              <Alert severity="warning" sx={{ fontSize: 13 }}>
                Other downloads are already using every slot on the server. Try
                again when one finishes.
              </Alert>
            )}

            <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
              The video and audio are copied, not re-encoded, into one MP4 that
              streams straight to your downloads folder. If the stream breaks
              partway, the browser marks the download as failed. You will never
              get a silently shortened file.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!plan || !plan.available || plan.busy}
          data-testid="vod-download-start"
          onClick={() => {
            if (!title) return;
            download(foyerApi.vodDownloadUrl(title.assetId));
            onClose();
          }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
